import * as React from 'react';
import { ChartContainer } from '@mui/x-charts/ChartContainer';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { ChartsClipPath } from '@mui/x-charts/ChartsClipPath';
import { ChartsAxisHighlight } from '@mui/x-charts/ChartsAxisHighlight';
import { LineHighlightPlot, LinePlot } from '@mui/x-charts/LineChart';
import { BarPlot } from '@mui/x-charts/BarChart';
import {
  dateTickFormat,
  dateFormat,
  elevationFormatter,
} from '../utils/format';
import { useHighlightedAxis } from './HighlightedAxisContext';
import NightOverlay from './NightOverlay';
import useId from '@mui/utils/useId';

function getLabel(label, elevation, elevationLabel) {
  if (elevationLabel === true || elevationLabel === undefined) {
    return `${elevationFormatter.format(elevation)} ft ${label}`;
  }
  return label;
}

export default function PrecipChart({
  forecast,
  xAxisPosition = 'bottom',
  height = 130,
}) {
  const { highlightedAxis, setHighlightedAxis } = useHighlightedAxis();
  const { elevation, times, data } = forecast;
  const id = useId();
  const clipPathId = `${id}-clip-path`;

  return (
    <ChartContainer
      xAxis={[
        {
          data: times,
          id: 'x-axis',
          domainLimit: 'strict',
          scaleType: 'time',
          valueFormatter: (date, context) =>
            context.location === 'tick'
              ? dateTickFormat(date)
              : dateFormat(date),
          position: xAxisPosition,
        },
        {
          data: times,
          id: 'x-axis-band',
          domainLimit: 'strict',
          scaleType: 'band',
          position: 'none',
        },
      ]}
      yAxis={[
        {
          id: 'left',
          width: 80,
          valueFormatter: (p) => `${p.toFixed(2)}"`,
        },
        {
          id: 'right',
          width: 60,
          position: 'right',
          valueFormatter: (p) => `${p}%`,
        },
      ]}
      series={[
        {
          type: 'bar',
          label: getLabel('WE', elevation, false),
          data: data.precipitation,
          valueFormatter: (p) => `${p.toFixed(2)}"`,
          showMark: false,
          color: '#c6dad8ff',
          yAxisId: 'left',
          xAxisId: 'x-axis-band',
        },
        {
          type: 'line',
          label: getLabel('Precip Prop', elevation, false),
          data: data.probabilityOfPrecipitation,
          valueFormatter: (p) => `${p}%`,
          showMark: false,
          color: '#28A197',
          yAxisId: 'right',
        },
      ]}
      height={xAxisPosition !== 'none' ? height : height - 20}
      highlightedAxis={highlightedAxis}
      onHighlightedAxisChange={setHighlightedAxis}
      margin={{
        bottom: xAxisPosition === 'bottom' ? 15 : 10,
        top: 15,
      }}
    >
      <g clipPath={`url(#${clipPathId})`}>
        <NightOverlay times={times} />
        <ChartsGrid horizontal vertical />
        <BarPlot />
        <LinePlot />
      </g>
      <ChartsClipPath id={clipPathId} />
      <LineHighlightPlot />
      <ChartsAxisHighlight x="line" />
      <ChartsXAxis />
      <ChartsYAxis axisId="left" label="WE" />
      <ChartsYAxis axisId="right" label="Precip Prob" />
      <ChartsTooltip />
    </ChartContainer>
  );
}
